import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Environment } from '~/engine/data/Environment';
import { PlaneView } from '~/engine/view/views/PlaneView';

export type PointMapping = (pointMm: ReadonlyVec3) => vec3 | null;

export function tieCrosshairs(
    primary: Environment,
    secondary: Environment,
    toSecondary: PointMapping,
    toPrimary: PointMapping
): () => void {
    // One flag for both directions, or each write maps straight back.
    let syncing = false;
    const follow =
        (target: Environment, mapPoint: PointMapping) =>
        (coords: ReadonlyVec3) => {
            if (syncing) {
                return;
            }
            const mapped = mapPoint(coords);
            if (!mapped) {
                return;
            }
            syncing = true;
            try {
                target.planeIntersectionPoint.coordinates = mapped;
            } finally {
                syncing = false;
            }
        };

    const primaryToSecondary = follow(secondary, toSecondary);
    const secondaryToPrimary = follow(primary, toPrimary);
    primary.planeIntersectionPoint.subscribe(primaryToSecondary);
    secondary.planeIntersectionPoint.subscribe(secondaryToPrimary);

    return () => {
        primary.planeIntersectionPoint.unsubscribe(primaryToSecondary);
        secondary.planeIntersectionPoint.unsubscribe(secondaryToPrimary);
    };
}

// Keeps each target view's own slice depth; only the in-plane centre follows.
export function mirrorViewPositions(
    pairs: readonly (readonly [PlaneView | null, PlaneView | null])[],
    mapPoint: PointMapping
): void {
    pairs.forEach(([from, to]) => {
        if (!from || !to) {
            return;
        }
        const mapped = mapPoint(from.position);
        if (!mapped) {
            return;
        }
        const direction = to.viewDirection;
        const next = vec3.scaleAndAdd(
            vec3.create(),
            mapped,
            direction,
            vec3.dot(to.position, direction) - vec3.dot(mapped, direction)
        );
        if (vec3.exactEquals(next, to.position)) {
            return;
        }
        to.setViewPosition(next);
        to.updateView();
    });
}
